import React from "react";
import { type TFunction } from "i18next";
import cn from "classnames";
import { Activity } from "iconsax-react";
import { Avatar, Badge } from "@/shared/ui";
import { formatDateHuman } from "@/shared/utils";
import type { Locale } from "@/shared/utils";
import type { AuditEntry, AuditFieldChange } from "@/shared/types/audit";

export interface AuditEntryItemConfig {
  actionLabelMap: Record<string, string>;
  iconMap: Record<string, React.ComponentType<{ size: number; color: string }>>;
  renderFieldValue?: (
    field: string,
    value: unknown,
    displayValue: string | undefined,
    t: TFunction,
  ) => React.ReactNode | undefined;
  renderFieldChange?: (
    field: string,
    change: AuditFieldChange,
    label: string,
    t: TFunction,
  ) => React.ReactNode | undefined;
  renderMetadata?: (entry: AuditEntry, t: TFunction) => React.ReactNode | null;
  getEntryNote?: (entry: AuditEntry) => string | null;
  getActionLabelKey?: (entry: AuditEntry) => string | undefined;
  getLabelParams?: (entry: AuditEntry, locale: Locale) => Record<string, string> | undefined;
  filteredChangeKeys?: string[];
}

interface Props {
  entry: AuditEntry;
  isLast: boolean;
  namespace: string;
  config: AuditEntryItemConfig;
  t: TFunction;
  locale: Locale;
}

function isFieldChange(value: unknown): value is AuditFieldChange {
  if (!value || typeof value !== "object") return false;
  return "old" in value && "new" in value;
}

function getFieldLabel(field: string, namespace: string, t: TFunction): string {
  const key = `${namespace}.fieldLabel.${field}`;
  const translated = t(key);
  if (translated !== key) return translated;
  return field;
}

function isEmptyFieldValue(value: unknown): boolean {
  return value === null || value === undefined || value === "";
}

function getDefaultFieldDisplayValue(
  value: unknown,
  displayValue: string | undefined,
  namespace: string,
  t: TFunction,
) {
  const display = displayValue ?? String(value ?? "");
  if (!display) {
    return <span className="text-body-regular-sm content-base-secondary italic">{t(`${namespace}.noValue`)}</span>;
  }
  return <span className="text-body-bold-sm content-base-primary">{display}</span>;
}

function getIcon(action: string, iconMap: Record<string, React.ComponentType<{ size: number; color: string }>>) {
  const prefix = action.split(".")[0];
  return iconMap[prefix] ?? Activity;
}

export default function AuditEntryItem({ entry, isLast, namespace, config, t, locale }: Props) {
  const labelKey =
    config.getActionLabelKey?.(entry) ?? config.actionLabelMap[entry.action] ?? `${namespace}.unknownAction`;
  const IconComponent = getIcon(entry.action, config.iconMap);
  const isSystem = entry.actor_type !== "user";

  const filteredKeys = config.filteredChangeKeys ?? ["review_action", "snapshot", "position"];

  function getFieldDisplayValue(field: string, value: unknown, displayValue: string | undefined) {
    if (config.renderFieldValue) {
      const custom = config.renderFieldValue(field, value, displayValue, t);
      if (custom !== undefined) return custom;
    }
    return getDefaultFieldDisplayValue(value, displayValue, namespace, t);
  }

  function renderMetadata() {
    if (config.renderMetadata) {
      return config.renderMetadata(entry, t);
    }
    return null;
  }

  function renderChanges() {
    if (!entry.changes) return null;

    if (entry.changes.status && isFieldChange(entry.changes.status) && !filteredKeys.includes("status")) {
      const statusChange = entry.changes.status;
      const otherFields = Object.entries(entry.changes).filter(
        ([key]) => !filteredKeys.includes(key) && key !== "status",
      );

      return (
        <>
          <div className="mt-1.5 flex items-center gap-1.5">
            {getFieldDisplayValue("status", statusChange.old, statusChange.old_display)}
            <span className="text-body-regular-xs content-base-secondary">→</span>
            {getFieldDisplayValue("status", statusChange.new, statusChange.new_display)}
          </div>
          {otherFields.length > 0 && (
            <div className="mt-1.5 flex flex-col gap-1.5">
              {otherFields.map(([field, change]) => {
                if (!isFieldChange(change)) return null;
                const label = getFieldLabel(field, namespace, t);
                return (
                  <div key={field} className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-body-regular-xs content-base-secondary shrink-0">{label}:</span>
                    {getFieldDisplayValue(field, change.old, change.old_display)}
                    <span className="text-body-regular-xs content-base-secondary">→</span>
                    {getFieldDisplayValue(field, change.new, change.new_display)}
                  </div>
                );
              })}
            </div>
          )}
        </>
      );
    }

    const fields = Object.entries(entry.changes).filter(([key]) => !filteredKeys.includes(key));

    if (fields.length === 0) return null;

    return (
      <div className="mt-1.5 flex flex-col gap-1.5">
        {fields.map(([field, change]) => {
          if (!isFieldChange(change)) return null;

          const label = getFieldLabel(field, namespace, t);

          if (config.renderFieldChange) {
            const custom = config.renderFieldChange(field, change, label, t);
            if (custom !== undefined) return <React.Fragment key={field}>{custom}</React.Fragment>;
          }

          if (isEmptyFieldValue(change.new)) {
            return (
              <p key={field} className="text-body-regular-xs content-base-secondary">
                {t(`${namespace}.fieldCleared`, { label })}
              </p>
            );
          }

          if (isEmptyFieldValue(change.old)) {
            const newDisplay = change.new_display ?? String(change.new ?? "");
            return (
              <p key={field} className="text-body-regular-xs content-base-secondary">
                {t(`${namespace}.fieldSet`, { label, value: newDisplay })}
              </p>
            );
          }

          return (
            <div key={field} className="flex items-center gap-1.5 flex-wrap">
              <span className="text-body-regular-xs content-base-secondary shrink-0">{label}:</span>
              {getFieldDisplayValue(field, change.old, change.old_display)}
              <span className="text-body-regular-xs content-base-secondary">→</span>
              {getFieldDisplayValue(field, change.new, change.new_display)}
            </div>
          );
        })}
      </div>
    );
  }

  const inlineNote = config.getEntryNote?.(entry) ?? null;

  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center shrink-0">
        <div className="w-7 h-7 rounded-full surface-component-stroke-fill flex items-center justify-center shrink-0 content-base-secondary">
          <IconComponent size={16} color="currentColor" />
        </div>
        {!isLast && <div className="w-px flex-1 surface-component-stroke-fill" />}
      </div>

      <div className={cn("flex-1 min-w-0 pb-5", isLast && "pb-0")}>
        <p className="text-body-bold-sm content-base-primary">
          {t(labelKey, config.getLabelParams?.(entry, locale))}
          {inlineNote && (
            <>
              {": "}
              <span className="font-normal content-base-secondary">«{inlineNote}»</span>
            </>
          )}
        </p>

        <p className="text-body-regular-xs content-base-secondary mt-0.5">{formatDateHuman(entry.timestamp, locale)}</p>

        <div className="mt-1.5">
          {isSystem ? (
            <Badge variant="soft" color="gray" text={t(`${namespace}.systemActor`)} />
          ) : (
            <div className="relative group/avatar inline-block">
              <Avatar name={entry.actor_name} size={24} textClassName="text-[10px]" />
              <div className="absolute hidden group-hover/avatar:block left-full top-1/2 -translate-y-1/2 ml-2 px-2 py-1 radius-xs surface-base-fill elevation-level-2 text-label-xs content-base-primary whitespace-nowrap z-10">
                {entry.actor_name}
              </div>
            </div>
          )}
        </div>

        {renderChanges()}
        {renderMetadata()}
      </div>
    </div>
  );
}

